import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PublicHeader, Logo } from "@/components/layout/AppChrome";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5">
        {/* Brand hero — visible on lg+; sits on the START (left in LTR, right in RTL) */}
        <div
          data-testid="auth-brand-hero"
          className="hidden lg:block lg:col-span-2 relative overflow-hidden"
          style={{
            backgroundImage: 'url(/brand/hero-brand-full.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Warm cinematic scrim so text over the image (if any) stays legible */}
          <div className="absolute inset-0"
               style={{ background: 'linear-gradient(140deg, rgba(15,10,4,0.05) 0%, rgba(15,10,4,0.35) 100%)' }} />
          {/* Bottom-left brand caption */}
          <div className="absolute inset-x-0 bottom-0 p-8 text-white">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-80" style={{ color:'#EACB6E' }}>
              DAAM · دعم
            </div>
            <div className="mt-2 text-xl font-semibold max-w-xs leading-snug">
              نظام تشغيل النمو الرقمي
            </div>
            <div className="mt-1 text-sm opacity-80 max-w-xs">
              AI-powered digital growth OS for the GCC
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="lg:col-span-3 flex items-center justify-center px-4 py-16">
          <div className="w-full max-w-md nx-card p-8 md:p-10 animate-fade-in-up">
            <div className="lg:hidden mb-6 flex justify-center">
              <span
                data-testid="auth-brand-wordmark"
                className="font-display font-semibold text-4xl tracking-tight"
                style={{
                  background: "linear-gradient(135deg,#F7E29F 0%,#EACB6E 25%,#C79A45 55%,#9E7326 85%,#6E4B16 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                DAAM
              </span>
            </div>
            <h1 className="font-display text-3xl">{title}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-6 text-sm text-center text-muted-foreground">{footer}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block mb-4">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <input
        data-testid={testId}
        className="mt-2 w-full bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary transition outline-none"
        {...props}
      />
    </label>
  );
}

export function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Honour the return path captured by <Protected> (e.g. a deep-link to
  // /app/preview/video-pilot). Only accept internal app paths so we can't
  // be redirected off-domain via a crafted state.
  const fromPath = loc.state?.from?.pathname;
  const safeFrom =
    typeof fromPath === "string" && fromPath.startsWith("/app/") ? fromPath : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    const role = res.user.role;
    if (role === "client" && !res.user.onboarding_completed) {
      nav("/onboarding");
    } else if (safeFrom) {
      nav(safeFrom, { replace: true });
    } else {
      nav("/app/dashboard");
    }
  };

  return (
    <AuthShell title={t.auth.loginTitle} subtitle={t.auth.loginSubtitle}
      footer={<>{t.auth.noAccount} <Link to="/register" data-testid="login-signup-link" className="text-primary hover:underline">{t.common.signUp}</Link></>}>
      <form onSubmit={submit} data-testid="login-form">
        <Field label={t.common.email} testId="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label={t.common.password} testId="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <div data-testid="login-error" className="text-sm text-destructive mb-4">{error}</div>}
        <button data-testid="login-submit-btn" type="submit" disabled={busy} className="nx-btn-primary w-full">{busy ? t.common.loading : t.common.signIn}</button>
        <div className="mt-4 text-center">
          <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm text-muted-foreground hover:text-primary">{t.auth.forgotLink}</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Password strength meter
  const rules = [
    { key: "len", label: "8+ characters", ok: password.length >= 8 },
    { key: "up", label: "Uppercase", ok: /[A-Z]/.test(password) },
    { key: "low", label: "Lowercase", ok: /[a-z]/.test(password) },
    { key: "num", label: "Number", ok: /\d/.test(password) },
  ];
  const strength = rules.filter((r) => r.ok).length;
  const strengthColor = ["bg-destructive", "bg-destructive", "bg-amber-500", "bg-emerald-400", "bg-emerald-500"][strength];
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await register(name, email, password);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    nav("/onboarding");
  };

  return (
    <AuthShell title={t.auth.registerTitle} subtitle={t.auth.registerSubtitle}
      footer={<>{t.auth.haveAccount} <Link to="/login" data-testid="register-signin-link" className="text-primary hover:underline">{t.common.signIn}</Link></>}>
      <form onSubmit={submit} data-testid="register-form">
        <Field label={t.common.name} testId="register-name-input" required value={name} onChange={(e) => setName(e.target.value)} />
        <Field label={t.common.email} testId="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label={t.common.password} testId="register-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        {password && (
          <div data-testid="password-strength" className="mb-4 -mt-2">
            <div className="flex gap-1 mb-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`h-1 flex-1 rounded-sm ${i < strength ? strengthColor : "bg-muted"}`} />
              ))}
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{strengthLabel}</span>
              <div className="flex gap-2">
                {rules.map((r) => (
                  <span key={r.key} data-testid={`pw-rule-${r.key}`} className={`${r.ok ? "text-emerald-500" : "text-muted-foreground"}`}>
                    {r.ok ? "✓" : "○"} {r.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        {error && <div data-testid="register-error" className="text-sm text-destructive mb-4">{error}</div>}
        <button data-testid="register-submit-btn" type="submit" disabled={busy || strength < 4} className="nx-btn-primary w-full disabled:opacity-50">{busy ? t.common.loading : t.common.getStarted}</button>
        {/* Legal consent — informational; account creation still requires Terms/Privacy compliance */}
        <p data-testid="register-legal-consent" className="mt-3 text-xs text-muted-foreground leading-relaxed">
          {t.common.byRegistering || "By creating an account you agree to our"}{" "}
          <Link to="/terms" className="underline hover:text-foreground">{t.common.terms || "Terms of Service"}</Link>
          {", "}
          <Link to="/privacy" className="underline hover:text-foreground">{t.common.privacy || "Privacy Policy"}</Link>
          {", "}
          <Link to="/refund" className="underline hover:text-foreground">{t.common.refund || "Refund Policy"}</Link>
          {" & "}
          <Link to="/cancellation" className="underline hover:text-foreground">{t.common.cancellation || "Cancellation Policy"}</Link>.
        </p>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.dev_reset_link || true);
      toast.success("Check your email for the reset link.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={t.auth.forgotTitle} subtitle={t.auth.forgotSubtitle}
      footer={<Link to="/login" data-testid="forgot-back-link" className="text-primary hover:underline">{t.common.signIn}</Link>}>
      <form onSubmit={submit} data-testid="forgot-form">
        <Field label={t.common.email} testId="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="nx-btn-primary w-full">{busy ? t.common.loading : t.common.submit}</button>
        {sent && sent !== true && (
          <div className="mt-4 text-xs text-muted-foreground p-3 nx-card break-all" data-testid="forgot-dev-link">
            <div className="mb-1 font-medium">{t.auth.devResetLink}</div>
            <a href={sent} className="text-primary hover:underline">{sent}</a>
          </div>
        )}
      </form>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { setError("Missing token"); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title={t.auth.resetTitle} subtitle={t.auth.resetSubtitle}
      footer={<Link to="/login" className="text-primary hover:underline">{t.common.signIn}</Link>}>
      <form onSubmit={submit} data-testid="reset-form">
        <Field label={t.common.password} testId="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <div data-testid="reset-error" className="text-sm text-destructive mb-4">{error}</div>}
        <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="nx-btn-primary w-full">{busy ? t.common.loading : t.common.save}</button>
      </form>
    </AuthShell>
  );
}
